import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Global cancel event for Universe loading.
 * Use this when modals/back buttons need to cancel Universe loading state.
 */
export const UNIVERSE_CANCEL_EVENT = 'UL_CANCEL_UNIVERSE_LOADING';

export function cancelUniverseLoading(detail = {}) {
  // Prefer CustomEvent so we can pass debug info; fallback to Event.
  try {
    window.dispatchEvent(new CustomEvent(UNIVERSE_CANCEL_EVENT, { detail }));
  } catch {
    window.dispatchEvent(new Event(UNIVERSE_CANCEL_EVENT));
  }
}

/**
 * Hook to listen for global cancel events
 */
export function useUniverseCancelListener(cancelFn) {
  useEffect(() => {
    if (!cancelFn) return;

    const handler = (e) => cancelFn(e?.detail || {});
    window.addEventListener(UNIVERSE_CANCEL_EVENT, handler);
    return () => window.removeEventListener(UNIVERSE_CANCEL_EVENT, handler);
  }, [cancelFn]);
}

/**
 * useSafeLoader - Hook for safe async loading with timeout, abort, and error handling
 */
export function useSafeLoader(loaderFn, options = {}) {
  const { timeoutMs = 15000, onError } = options;

  const abortRef = useRef(null);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const lastArgsRef = useRef(null);
  const runIdRef = useRef(0); // prevents stale resolution

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const cleanup = useCallback(() => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch {}
      abortRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    runIdRef.current += 1; // invalidate in-flight
    cleanup();
    if (mountedRef.current) setLoading(false);
  }, [cleanup]);

  const run = useCallback(async (...args) => {
    // Cancel any prior run
    cancel();
    lastArgsRef.current = args;

    const myRunId = ++runIdRef.current;

    const controller = new AbortController();
    abortRef.current = controller;

    if (mountedRef.current) {
      setError(null);
      setLoading(true);
    }

    timeoutRef.current = setTimeout(() => {
      if (controller.signal.aborted) return;
      try { controller.abort(); } catch {}
      if (mountedRef.current && myRunId === runIdRef.current) {
        const timeoutError = new Error(`Request timed out after ${timeoutMs / 1000}s. Please retry.`);
        setError(timeoutError);
        setLoading(false);
        onError?.(timeoutError);
      }
    }, timeoutMs);

    try {
      const result = await loaderFn({ signal: controller.signal }, ...args);

      if (!controller.signal.aborted && mountedRef.current && myRunId === runIdRef.current) {
        setData(result);
        setError(null);
      }
      return result;
    } catch (e) {
      if (e?.name === 'AbortError') return null;

      if (mountedRef.current && myRunId === runIdRef.current) {
        setError(e);
        onError?.(e);
      }
      return null;
    } finally {
      cleanup();
      if (mountedRef.current && myRunId === runIdRef.current) {
        setLoading(false);
      }
    }
  }, [loaderFn, timeoutMs, cleanup, onError, cancel]);

  const retry = useCallback(() => {
    if (lastArgsRef.current) return run(...lastArgsRef.current);
    return run();
  }, [run]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  return {
    run,
    cancel,
    retry,
    loading,
    error,
    data,
    clearError,
    setError,
  };
}

export default useSafeLoader;
